import React, { useMemo, useRef, useState } from 'react';
import { Card, Steps, Space, Button, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import DebtInfoAndDebtorSelectorCard from './PublishNoticeEditContent/DebtInfoAndDebtorSelectorCard';
import NoticeContentEditorSection from './PublishNoticeEditContent/NoticeContentEditorSection';
import NoticePreviewModal from './PublishNoticeEditContent/NoticePreviewModal';
import { loadState, saveState } from './PublishNoticeEditContent/state';
import { useDebtorSelection } from './PublishNoticeEditContent/useDebtorSelection';
import { usePlaceholderBinding } from './PublishNoticeEditContent/usePlaceholderBinding';
import { useBreakpoint } from '../../hooks/useBreakpoint';

/**
 * 催告发布步骤3：编辑内容
 */
const PublishNoticeEditContentPage = () => {
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();
  const { id } = useParams();
  const editorActionRef = useRef(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const {
    selectedDebt,
    rowByKey,
    selectedDebtorKeys,
    debtorViewType,
    setDebtorViewType,
    currentDebtorRows,
    debtorTypeOptions,
    applySelectedDebtors,
    showCreditorPersonal,
    showCreditorEnterprise,
    showDebtorPersonal,
    showDebtorEnterprise,
  } = useDebtorSelection({ id, navigate, setPreviewIndex });

  const prev = () => {
    const state = loadState();
    const debtId = id || state.selectedDebtId;
    navigate(`/debt-notice/publish/choose-template/${debtId}`);
  };

  const next = () => {
    const state = loadState();
    const debtId = id || state.selectedDebtId;
    if (!state.saved) {
      message.warning('请先校验并保存内容');
      return;
    }
    if (!Array.isArray(state.selectedDebtorKeys) || state.selectedDebtorKeys.length === 0) {
      message.warning('请至少选择一个债务人');
      return;
    }
    navigate(`/debt-notice/publish/signature/${debtId}`);
  };

  const handleSaved = () => {
    saveState({ saved: true });
  };
  const {
    placeholders,
    setPlaceholders,
    placeholderRows,
    fieldOptions,
    handleSmartMatchFields,
    handleReloadTemplate,
    setPlaceholderFieldFor,
    setPlaceholderManualFor,
    buildPlaceholderValuesForDebtor,
  } = usePlaceholderBinding({
    selectedDebt,
    showCreditorPersonal,
    showCreditorEnterprise,
    showDebtorPersonal,
    showDebtorEnterprise,
    editorActionRef,
    setPreviewOpen,
    setPreviewIndex,
  });

  const selectedDebtors = useMemo(() => selectedDebtorKeys.map(k => rowByKey[k]).filter(Boolean), [rowByKey, selectedDebtorKeys]);
  const previewDebtor = useMemo(() => {
    if (selectedDebtors.length === 0) return null;
    const idx = Math.min(Math.max(0, previewIndex), selectedDebtors.length - 1);
    return selectedDebtors[idx] || selectedDebtors[0] || null;
  }, [selectedDebtors, previewIndex]);
  const debtorOptions = useMemo(() => selectedDebtors.map((d, idx) => ({
    value: idx,
    label: d.type === 'P' ? `${d.name || '个人'} (${d.idNo || '-'})` : `${d.companyName || '企业'} (${d.socialCreditCode || '-'})`,
  })), [selectedDebtors]);
  const previewValues = useMemo(() => (previewDebtor ? buildPlaceholderValuesForDebtor(previewDebtor) : {}), [buildPlaceholderValuesForDebtor, previewDebtor]);

  const steps = [
    { title: '选择债权' },
    { title: '选择模板' },
    { title: '编辑内容' },
    { title: '签章与预览' },
    { title: '生成催告函' },
    { title: '发送催告' },
    { title: '归档' },
  ];
  const currentStep = 2;

  return (
    <div className="page-container">
      <div className="page-content page-content-wide">
        <h1 className="page-title">发布催告</h1>

        <Card className="card-compact" size="small">
          {isMobile ? (
            <div className="c-steps-mobile-header" aria-label="步骤信息">
              <div className="c-steps-mobile-left">
                <span className="c-steps-mobile-stepno">{`第 ${currentStep + 1} 步`}</span>
                <span className="c-steps-mobile-title">{steps[currentStep]?.title || ''}</span>
              </div>
              <div className="c-steps-mobile-progress">{`${currentStep + 1}/${steps.length}`}</div>
            </div>
          ) : (
            <Steps current={currentStep} size="small" items={steps} />
          )}
        </Card>

        <div className="mb-md">
          <Space
            direction={isMobile ? 'vertical' : 'horizontal'}
            wrap={!isMobile}
            style={{ width: isMobile ? '100%' : undefined }}
          >
            <Button onClick={prev} block={isMobile}>上一步</Button>
            <Button type="primary" onClick={next} block={isMobile}>下一步</Button>
          </Space>
        </div>

      {selectedDebt && (
        <Card className="mb-md">
          <DebtInfoAndDebtorSelectorCard
            selectedDebt={selectedDebt}
            debtorTypeOptions={debtorTypeOptions}
            debtorViewType={debtorViewType}
            setDebtorViewType={setDebtorViewType}
            selectedDebtorKeys={selectedDebtorKeys}
            rowByKey={rowByKey}
            currentDebtorRows={currentDebtorRows}
            applySelectedDebtors={applySelectedDebtors}
          />
        </Card>
      )}

      <Card>
        <NoticeContentEditorSection
          placeholders={placeholders}
          placeholderRows={placeholderRows}
          fieldOptions={fieldOptions}
          selectedDebtorCount={selectedDebtorKeys.length}
          onSmartMatchFields={handleSmartMatchFields}
          onOpenPreview={() => setPreviewOpen(true)}
          onSave={() => editorActionRef.current?.save?.()}
          onReloadTemplate={handleReloadTemplate}
          setPlaceholderFieldFor={setPlaceholderFieldFor}
          setPlaceholderManualFor={setPlaceholderManualFor}
          setPlaceholders={setPlaceholders}
          editorActionRef={editorActionRef}
          onSaved={handleSaved}
        />
      </Card>

      <NoticePreviewModal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        selectedDebtors={selectedDebtors}
        previewIndex={previewIndex}
        setPreviewIndex={setPreviewIndex}
        debtorOptions={debtorOptions}
        previewDebtor={previewDebtor}
        previewValues={previewValues}
      />
      </div>
    </div>
  );
};

export default PublishNoticeEditContentPage;
